// =====================================================================
// mappers.js — single source of truth between the PocketBase schema
// (docs/backend.md §4) and the UI shapes the screens consume (the
// mockData.js contract). Both directions live here so nothing drifts.
//
// UI shapes are exactly what the screens were built against in
// mockData.js — this module is what lets 20+ screens go live with zero
// structural changes.
// =====================================================================

// --- tenant / settings --------------------------------------------------

export const DEFAULT_TENANT = {
  id: '',
  name: 'Client Portal',
  slug: '',
  logo: '',
  primary_color: '#173e36',
  accent_color: '#dfff73',
  contact_email: '',
  contact_phone: '',
  telegram_chat_id: '',
  status: 'active',
  address: '',
  invoice_prefix: 'INV-',
  next_invoice_number: 1000,
  next_certificate_number: 1,
  payment_terms: '14',
  tax_rate: 0,
  payment_instructions: '',
  sms_enabled: false,
  key_date_types: [],
  stripe_account_id: '',
  google_review_url: '',
  review_request_enabled: false,
  review_request_delay_hours: 4,
  ai_enabled: false,
  ai_api_key: '',
  ai_model: 'openai/gpt-4o-mini',
  ai_base_url: 'https://openrouter.ai/api/v1',
};

export function mapTenant(rec) {
  return {
    id: rec.id,
    name: rec.name || '',
    slug: rec.slug || '',
    logo: rec.logo || '',
    primary_color: rec.primary_color || DEFAULT_TENANT.primary_color,
    accent_color: rec.accent_color || DEFAULT_TENANT.accent_color,
    contact_email: rec.contact_email || '',
    contact_phone: rec.contact_phone || '',
    telegram_chat_id: rec.telegram_chat_id || '',
    status: rec.status || 'active',
    address: rec.address || '',
    invoice_prefix: rec.invoice_prefix || 'INV-',
    next_invoice_number: rec.next_invoice_number || 1000,
    next_certificate_number: rec.next_certificate_number || 1,
    payment_terms: rec.payment_terms || '14',
    tax_rate: Number(rec.tax_rate) || 0,
    payment_instructions: rec.payment_instructions || '',
    sms_enabled: Boolean(rec.sms_enabled),
    key_date_types: Array.isArray(rec.key_date_types) ? rec.key_date_types : [],
    stripe_account_id: rec.stripe_account_id || '',
    google_review_url: rec.google_review_url || '',
    review_request_enabled: Boolean(rec.review_request_enabled),
    review_request_delay_hours: Number(rec.review_request_delay_hours) || 4,
    ai_enabled: Boolean(rec.ai_enabled),
    ai_api_key: rec.ai_api_key || '',
    ai_model: rec.ai_model || DEFAULT_TENANT.ai_model,
    ai_base_url: rec.ai_base_url || DEFAULT_TENANT.ai_base_url,
  };
}

export function buildSettings(tenant) {
  return {
    business: {
      name: tenant.name,
      address: tenant.address,
      contact_email: tenant.contact_email,
      contact_phone: tenant.contact_phone,
      logo: tenant.logo,
    },
    branding: { primary_color: tenant.primary_color },
    notifications: {
      telegram_chat_id: tenant.telegram_chat_id,
      email: tenant.contact_email,
      sms_enabled: tenant.sms_enabled,
    },
    reviews: {
      google_review_url: tenant.google_review_url,
      enabled: tenant.review_request_enabled,
      delay_hours: tenant.review_request_delay_hours,
    },
    ai: {
      enabled: tenant.ai_enabled,
      api_key: tenant.ai_api_key,
      model: tenant.ai_model || 'openai/gpt-4o-mini',
      base_url: tenant.ai_base_url || 'https://openrouter.ai/api/v1',
    },
    invoice: {
      prefix: tenant.invoice_prefix,
      next_number: tenant.next_invoice_number,
      terms: tenant.payment_terms,
      tax_rate: tenant.tax_rate,
      instructions: tenant.payment_instructions,
    },
    keyDateTypes: tenant.key_date_types,
  };
}

export function defaultSettings() {
  return buildSettings(DEFAULT_TENANT);
}

// settings (UI) -> tenants record patch
export function settingsToTenantPatch(settings) {
  const business = settings.business || {};
  const branding = settings.branding || {};
  const notifications = settings.notifications || {};
  const reviews = settings.reviews || {};
  const ai = settings.ai || {};
  const invoice = settings.invoice || {};
  return {
    name: business.name,
    address: business.address,
    contact_email: notifications.email || business.contact_email,
    contact_phone: business.contact_phone,
    primary_color: branding.primary_color,
    telegram_chat_id: notifications.telegram_chat_id,
    sms_enabled: Boolean(notifications.sms_enabled),
    google_review_url: reviews.google_review_url || '',
    review_request_enabled: Boolean(reviews.enabled),
    review_request_delay_hours: Number(reviews.delay_hours) || 4,
    ai_enabled: Boolean(ai.enabled),
    ai_api_key: ai.api_key || '',
    ai_model: ai.model || 'openai/gpt-4o-mini',
    ai_base_url: ai.base_url || 'https://openrouter.ai/api/v1',
    invoice_prefix: invoice.prefix,
    next_invoice_number: Number(invoice.next_number) || 1000,
    payment_terms: String(invoice.terms || '14'),
    tax_rate: Number(invoice.tax_rate) || 0,
    payment_instructions: invoice.instructions,
    key_date_types: settings.keyDateTypes || [],
  };
}

// --- clients ------------------------------------------------------------

export function mapClient(rec) {
  const name = rec.name || '';
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    name,
    initials: initials(name),
    email: rec.email || '',
    phone: rec.phone || '',
    // Structured address (schema §4) + derived single-line for the many
    // display surfaces (hero lines, lists, CSV, invoice bill-to).
    address_line1: rec.address_line1 || '',
    address_line2: rec.address_line2 || '',
    city: rec.city || '',
    postcode: rec.postcode || '',
    country: rec.country || '',
    lat: rec.lat ?? null,
    lng: rec.lng ?? null,
    address: formatAddress(rec),
    notes: rec.notes || '',
    status: rec.status || 'active',
  };
}

export function clientToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    name: ui.name,
    email: ui.email || '',
    phone: ui.phone || '',
    address_line1: ui.address_line1 || '',
    address_line2: ui.address_line2 || '',
    city: ui.city || '',
    postcode: ui.postcode || '',
    country: ui.country || '',
    lat: ui.lat ?? null,
    lng: ui.lng ?? null,
    notes: ui.notes || '',
    status: ui.status || 'active',
  };
}

export function formatAddress(rec) {
  return [rec.address_line1, rec.address_line2, rec.city, rec.postcode]
    .filter(Boolean)
    .join(', ');
}

// --- properties ----------------------------------------------------------
// Multi-property clients (PLAN_admin_portal_phase1.md §4): a client can own
// many properties; jobs carry a nullable property_id plus their own site_*
// snapshot (the site is the display/location truth — the relation adds
// structure for reuse + default address prefill).

export function mapProperty(rec) {
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    client_id: rec.client_id || '',
    name: rec.name || '',
    address_line1: rec.address_line1 || '',
    address_line2: rec.address_line2 || '',
    city: rec.city || '',
    postcode: rec.postcode || '',
    country: rec.country || '',
    lat: rec.lat ?? null,
    lng: rec.lng ?? null,
    address: formatAddress(rec),
    notes: rec.notes || '',
    status: rec.status || 'active',
    client_name: rec.expand?.client_id?.name || '',
  };
}

export function propertyToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    client_id: ui.client_id,
    name: ui.name || '',
    address_line1: ui.address_line1 || '',
    address_line2: ui.address_line2 || '',
    city: ui.city || '',
    postcode: ui.postcode || '',
    country: ui.country || '',
    lat: ui.lat ?? null,
    lng: ui.lng ?? null,
    notes: ui.notes || '',
    status: ui.status || 'active',
  };
}

// --- jobs ---------------------------------------------------------------

// Datetime convention: naive local `YYYY-MM-DDTHH:mm:ss` strings, no
// timezone suffix — stored verbatim by PocketBase, parsed with
// `new Date(str)` (local per JS spec). See migration 007.

export function mapJob(rec) {
  const status = rec.status || 'scheduled';
  return {
    id: rec.id,
    client_id: rec.client_id,
    title: rec.title || '',
    description: rec.description || '',
    status,
    // Display date: completed jobs anchor on their completion date,
    // everything else on the scheduled window's start day.
    date: status === 'completed' && rec.date_completed
      ? String(rec.date_completed).slice(0, 10)
      : rec.scheduled_start ? String(rec.scheduled_start).slice(0, 10) : '',
    scheduled_start: rec.scheduled_start || '',
    scheduled_end: rec.scheduled_end || '',
    // engineer is a relation → engineers; keep the display name resolved
    // from the expand, plus the raw id for filtering/writes.
    engineer_id: rec.engineer || '',
    engineer: rec.expand?.engineer?.name || '',
    engineer_color: rec.expand?.engineer?.color || '',
    notes: rec.notes || '',
    // Raw photo filenames — URLs are minted fresh at render/click time
    // (pb.js protectedFileUrl) because file tokens live ~2 minutes.
    photos: Array.isArray(rec.photos) ? rec.photos.filter(Boolean) : [],
    site_line1: rec.site_address_line1 || '',
    site_line2: rec.site_address_line2 || '',
    site_city: rec.site_city || '',
    site_postcode: rec.site_postcode || '',
    site_country: rec.site_country || '',
    site_lat: rec.site_lat ?? null,
    site_lng: rec.site_lng ?? null,
    // Linked property (migration 021): raw id for writes, display name +
    // coords resolved from the expand. Property coords are the map fallback
    // when a job has no site coordinates of its own.
    property_id: rec.property_id || '',
    property_name: rec.expand?.property_id?.name || '',
    property_lat: rec.expand?.property_id?.lat ?? null,
    property_lng: rec.expand?.property_id?.lng ?? null,
    // Pass 6 self-service: client reschedule-request metadata
    client_requested_date: rec.client_requested_date || '',
    client_request_note: rec.client_request_note || '',
  };
}

export function jobToSchema(ui, tenantId) {
  const body = {
    tenant_id: tenantId,
    client_id: ui.client_id,
    title: ui.title,
    description: ui.description || '',
    status: ui.status || 'scheduled',
    engineer: ui.engineer_id || undefined, // Changed from empty string to undefined for relation field
    // Nullable relation — '' clears it, an id links the job to the property.
    property_id: ui.property_id || null,
    notes: ui.notes || '',
    site_address_line1: ui.site_line1 || '',
    site_address_line2: ui.site_line2 || '',
    site_city: ui.site_city || '',
    site_postcode: ui.site_postcode || '',
    site_country: ui.site_country || '',
    site_lat: ui.site_lat ?? null,
    site_lng: ui.site_lng ?? null,
  };
  if (ui.scheduled_start) body.scheduled_start = ui.scheduled_start;
  if (ui.scheduled_end) body.scheduled_end = ui.scheduled_end;
  if (ui.status === 'completed') body.date_completed = ui.date_completed || ui.date || null;
  // Request metadata round-trips both ways: always send it when the UI
  // shape carries it (null clears a stale request when staff accept a
  // reschedule / schedule a request — otherwise PB keeps the old value).
  if (ui.client_requested_date !== undefined) body.client_requested_date = ui.client_requested_date || null;
  if (ui.client_request_note !== undefined) body.client_request_note = ui.client_request_note || null;
  return body;
}

// --- engineers ----------------------------------------------------------

export function mapEngineer(rec) {
  return {
    id: rec.id,
    name: rec.name || '',
    email: rec.email || '',
    phone: rec.phone || '',
    color: rec.color || '',
    active: rec.active !== false,
  };
}

// --- invoices -----------------------------------------------------------

export const DOC_TYPE_LABELS = {
  invoice: 'Invoice',
  estimate: 'Estimate',
  quote: 'Quotation',
};

export function mapInvoice(rec) {
  const items = Array.isArray(rec.line_items) ? rec.line_items : [];
  const firstLine = items[0]?.description || '';
  return {
    id: rec.id,
    client_id: rec.client_id,
    job_id: rec.job_id || null,
    docType: rec.doc_type || 'invoice',
    number: rec.invoice_number || '',
    title: rec.title || rec.expand?.job_id?.title || firstLine || rec.invoice_number || '',
    issued_at: rec.created,
    due_date: rec.due_date || '',
    status: rec.status || 'draft',
    linked_job_id: rec.job_id || null,
    line_items: items.map((item, index) => ({
      id: `${rec.id}_li_${index}`,
      description: item.description || '',
      quantity: Number(item.qty) || 0,
      unit_price: Number(item.unit_price) || 0,
      total: (Number(item.qty) || 0) * (Number(item.unit_price) || 0),
    })),
    paid_date: rec.paid_date || '',
    pdf: rec.pdf || '',
    // Passthrough share fields (task 1.3) — never sent to server via invoiceToSchema.
    shareToken: rec.share_token || '',
    shareUrl: rec.share_url || '',
  };
}

export function invoiceToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    client_id: ui.client_id,
    job_id: ui.linked_job_id || ui.job_id || null,
    doc_type: ui.docType || 'invoice',
    // NOTE: no `title` field — invoices has no such column (removed 2026-08-25;
    // PB used to ignore the unknown key silently).
    line_items: (ui.line_items || []).map((item) => ({
      description: item.description,
      qty: Number(item.quantity) || 1,
      unit_price: Number(item.unit_price) || 0,
    })),
    status: ui.status || 'draft',
    due_date: ui.due_date || null,
    paid_date: ui.paid_date || null,
  };
}

// --- payments -----------------------------------------------------------

export function mapPayment(rec, invoicesById) {
  const invoice = invoicesById[rec.invoice_id] || {};
  return {
    id: rec.id,
    client_id: invoice.client_id || rec.client_id || null,
    invoice_id: rec.invoice_id,
    amount: Number(rec.amount) || 0,
    method: rec.method || 'bank_transfer',
    status: rec.status || 'confirmed',
    date: rec.paid_at || '',
    reference: rec.reference || '',
    archived: Boolean(rec.archived),
  };
}

export function paymentToSchema(ui, tenantId) {
  const body = {
    tenant_id: tenantId,
    invoice_id: ui.invoice_id,
    amount: Number(ui.amount) || 0,
    method: ui.method || 'bank_transfer',
    status: ui.status || 'confirmed',
    paid_at: ui.date || null,
    reference: ui.reference || '',
  };
  // Reversal (spec §2.2): archiving a confirmed payment is the soft-delete;
  // the pb_hook recomputes the invoice's paid state excluding archived rows.
  if ('archived' in ui) body.archived = Boolean(ui.archived);
  return body;
}

// --- key dates ----------------------------------------------------------

export const KEY_DATE_TYPE_TO_UI = {
  boiler_service: 'service',
  gas_safety_cert: 'cert',
  upcoming_job: 'job',
  other: 'other',
};

export const KEY_DATE_TYPE_TO_SCHEMA = {
  service: 'boiler_service',
  cert: 'gas_safety_cert',
  job: 'upcoming_job',
  other: 'other',
};

const KEY_DATE_LABELS = {
  service: 'Boiler service',
  cert: 'Gas safety certificate',
  job: 'Upcoming job',
  other: 'Key date',
};

export function mapKeyDate(rec) {
  const type = KEY_DATE_TYPE_TO_UI[rec.type] || 'other';
  return {
    id: rec.id,
    client_id: rec.client_id,
    type,
    title: KEY_DATE_LABELS[type] || rec.notes || 'Key date',
    date: rec.due_date || '',
    detail: rec.notes || '',
    recurring: Boolean(rec.recurring),
  };
}

export function keyDateToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    client_id: ui.client_id,
    type: KEY_DATE_TYPE_TO_SCHEMA[ui.type] || 'other',
    due_date: ui.date || null,
    recurring: Boolean(ui.recurring),
    notes: ui.detail || ui.title || '',
  };
}

// --- messages -----------------------------------------------------------

export function mapMessage(rec) {
  return {
    id: rec.id,
    client_id: rec.client_id,
    sender: rec.sender || 'agency',
    name: rec.sender_name || '',
    body: rec.body || '',
    created_at: rec.created,
    read_at: rec.read_at || null,
  };
}

export function messageToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    client_id: ui.client_id,
    sender: ui.sender || 'agency',
    body: ui.body,
  };
}

// --- documents ----------------------------------------------------------

export function mapDocument(rec) {
  const file = Array.isArray(rec.file) ? rec.file[0] : rec.file || '';
  return {
    id: rec.id,
    client_id: rec.client_id,
    collectionName: 'documents',
    type: rec.type || 'other',
    filename: file || 'document',
    size: 0, // PB file fields don't expose size via the API — UI shows '—'
    uploaded_at: rec.created,
    version: rec.version || 1, // server-managed (audit.pb.js), 0 = unset → v1
    requiresAcknowledgement: Boolean(rec.requires_acknowledgement),
    archived: Boolean(rec.archived),
  };
}

// document_acknowledgements — client confirmations (tracking_tasks.md TRK-4.1)
export function mapAcknowledgement(rec) {
  return {
    id: rec.id,
    document_id: rec.document_id,
    client_id: rec.client_id,
    user_id: rec.user_id,
    document_name: rec.document_name || '',
    document_version: rec.document_version || '',
    acknowledged_text: rec.acknowledged_text || '',
    ack_type: rec.ack_type || 'read_confirm',
    acknowledged_at: rec.created,
  };
}

// --- notification log ---------------------------------------------------

export function mapNotification(rec) {
  return {
    id: rec.id,
    channel: rec.channel || 'telegram',
    recipient: rec.recipient || '',
    context: rec.context || '',
    status: rec.status || 'sent',
    created_at: rec.sent_at || rec.created,
  };
}

// --- team (accounts) ----------------------------------------------------

export function mapTeamMember(rec) {
  return {
    id: rec.id,
    name: rec.name || '',
    email: rec.email || '',
    role: rec.role || 'agency_staff',
    status: rec.status || 'active',
  };
}

// --- notes (E6) -----------------------------------------------------------

export function mapNote(rec) {
  return {
    id: rec.id,
    client_id: rec.client_id,
    author: rec.author_name || '',
    body: rec.body || '',
    pinned: Boolean(rec.pinned),
    client_visible: Boolean(rec.client_visible),
    created_at: rec.created,
  };
}

export function noteToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    client_id: ui.client_id,
    author_name: ui.author || '',
    body: ui.body,
    pinned: Boolean(ui.pinned),
    client_visible: Boolean(ui.client_visible),
  };
}

// --- quotes (pass 5) ------------------------------------------------------

// Quote status labels mirror StatusPill's display transformation
// (underscores → spaces) — statuses are the schema values.
export function mapQuote(rec) {
  const items = Array.isArray(rec.line_items) ? rec.line_items : [];
  return {
    id: rec.id,
    client_id: rec.client_id,
    number: rec.quote_number || '',
    title: items[0]?.description || rec.quote_number || 'Quote',
    status: rec.status || 'draft',
    valid_until: rec.valid_until || '',
    notes: rec.notes || '',
    approved_by_name: rec.approved_by_name || '',
    approved_at: rec.approved_at || '',
    converted_job_id: rec.converted_to_job_id || null,
    converted_invoice_id: rec.converted_to_invoice_id || null,
    issued_at: rec.created,
    line_items: items.map((item, index) => ({
      id: `${rec.id}_q_${index}`,
      description: item.description || '',
      quantity: Number(item.qty) || 0,
      unit_price: Number(item.unit_price) || 0,
      total: (Number(item.qty) || 0) * (Number(item.unit_price) || 0),
    })),
  };
}

export function quoteToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    client_id: ui.client_id,
    line_items: (ui.line_items || []).map((item) => ({
      description: item.description,
      qty: Number(item.quantity) || 1,
      unit_price: Number(item.unit_price) || 0,
    })),
    status: ui.status || 'draft',
    valid_until: ui.valid_until || null,
    notes: ui.notes || '',
  };
}

// --- certificates (pass 5) -------------------------------------------------

export const CERT_TYPE_LABELS = {
  // Original types
  gas_safety_cp12: 'Gas safety (CP12)',
  eicr: 'EICR (electrical)',
  f_gas: 'F-Gas',
  pat: 'PAT testing',
  loler: 'LOLER',
  g3_unvented: 'G3 unvented',
  other: 'Other',
  // Gas certificate types (docs/gas_certificates_build_spec.md §1)
  gsr: 'Gas Safety Record (GSR)',
  lgsr: 'Landlord Gas Safety Record (LGSR/CP12)',
  gsirep: 'Gas Safety Inspection Report (GSIREP)',
  gwn: 'Gas Warning Advice Notice (GWN)',
  gmsc: 'Gas Service Maintenance Checklist (GMSC)',
  gbsr: 'Gas Breakdown/Servicing Record (GBSR)',
  wpcr: 'Water Pipework Commissioning Record (WPCR)',
};

// Default validity per cert type (months) — mirrors the annual/other split
// used by the key-date sync hook; used to prefill the expiry field.
export const CERT_TYPE_VALIDITY_MONTHS = {
  // Original types
  gas_safety_cp12: 12,
  g3_unvented: 12,
  eicr: 60,
  f_gas: 12,
  pat: 12,
  loler: 6,
  other: 12,
  // Gas certificate types - LGSR is 12 months by regulation
  gsr: 12,
  lgsr: 12,
  gsirep: 12,
  gwn: 12,
  gmsc: 12,
  gbsr: 12,
  wpcr: 12,
};

// Safety classification labels (docs/gas_certificates_build_spec.md §1.4)
export const GAS_CLASSIFICATION_LABELS = {
  immediately_dangerous: 'Immediately Dangerous (ID)',
  at_risk: 'At Risk (AR)',
  not_to_current_standards: 'Not to Current Standards (NCS)',
  safe_to_use: 'Safe to Use',
};

// Safety classification order for validation (most severe first)
export const GAS_CLASSIFICATION_SEVERITY = {
  immediately_dangerous: 1,
  at_risk: 2,
  not_to_current_standards: 3,
  safe_to_use: 4,
};

export function mapCertificate(rec) {
  const file = Array.isArray(rec.file) ? rec.file[0] : rec.file || '';
  let details = {};
  if (rec.details) {
    try {
      details = typeof rec.details === 'string' ? JSON.parse(rec.details) : rec.details;
    } catch {
      details = {};
    }
  }
  // form_data is the canonical structured body (wizard); details is the
  // legacy pass-5 alias — prefer form_data, fall back to details.
  let formData = {};
  if (rec.form_data) {
    try {
      formData = typeof rec.form_data === 'string' ? JSON.parse(rec.form_data) : rec.form_data;
    } catch {
      formData = {};
    }
  }
  const body = Object.keys(formData).length ? formData : details;
  return {
    id: rec.id,
    collectionName: 'certificates', // for openProtectedFile/protectedFileUrl stubs
    client_id: rec.client_id,
    job_id: rec.job_id || null,
    engineer_job_id: rec.engineer_job_id || null,
    type: rec.type || 'other',
    typeLabel: CERT_TYPE_LABELS[rec.type] || 'Other',
    certificate_number: rec.certificate_number || '',
    issued_date: rec.issued_date || rec.issue_date || '',
    expiry_date: rec.expiry_date || '',
    classification: rec.classification || '',
    engineer_id: rec.engineer_id || '',
    engineer_signature_id: rec.engineer_signature_id || '',
    status: rec.status || 'draft',
    filename: file || '',
    photos: Array.isArray(rec.photos) ? rec.photos : [],
    notes: rec.notes || '',
    synced_key_date_id: rec.synced_key_date_id || '',
    uploaded_at: rec.created,
    details: body,
    form_data: body,
    applianceCount: Array.isArray(body.appliances) ? body.appliances.length : 0,
  };
}

// --- portal visits (access audit trail) --------------------------------------

export function mapPortalVisit(rec) {
  return {
    id: rec.id,
    client_id: rec.client_id,
    path: rec.path || '/',
    visited_at: rec.visited_at || rec.created,
  };
}

// --- public marketing sign-up leads (migration 025) --------------------------

export function mapSignup(rec) {
  return {
    id: rec.id,
    agency_name: rec.agency_name || '',
    contact_name: rec.contact_name || '',
    email: rec.email || '',
    phone: rec.phone || '',
    engineers: rec.engineers || '',
    tier: rec.tier || 'professional',
    notes: rec.notes || '',
    status: rec.status || 'new',
    created: rec.created,
  };
}

// --- audit events (tracking_tasks.md TRK-4.1) --------------------------------

export function mapAuditEvent(rec) {
  let metadata = {};
  if (rec.metadata) {
    try {
      metadata = typeof rec.metadata === 'string' ? JSON.parse(rec.metadata) : rec.metadata;
    } catch {
      metadata = {};
    }
  }
  return {
    id: rec.id,
    client_id: rec.client_id,
    user_id: rec.user_id || '',
    user_name: rec.expand?.user_id?.name || '',
    event_type: rec.event_type || '',
    entity_type: rec.entity_type || '',
    entity_id: rec.entity_id || '',
    entity_version: rec.entity_version || '',
    file_name: rec.file_name || '',
    method: rec.method || '',
    source: rec.source || '',
    ip: rec.ip || '',
    user_agent: rec.user_agent || '',
    idempotency_key: rec.idempotency_key || '',
    metadata,
    created_at: rec.created,
  };
}

// --- review requests (pass 7) ----------------------------------------------

export function mapReviewRequest(rec, jobsById = {}) {
  const job = jobsById[rec.job_id] || {};
  return {
    id: rec.id,
    client_id: rec.client_id,
    job_id: rec.job_id,
    job_title: job.title || '',
    channel: rec.channel || 'email',
    status: rec.status || 'sent',
    clicked: Boolean(rec.clicked),
    sent_at: rec.sent_at || rec.created,
  };
}

// --- pricing catalog (pass 7) -----------------------------------------------

export function mapPricingItem(rec) {
  return {
    id: rec.id,
    category: rec.category || '',
    name: rec.item_name || '',
    unit: rec.unit || '',
    price: Number(rec.default_unit_price) || 0,
    notes: rec.notes || '',
  };
}

export function pricingItemToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    category: ui.category || '',
    item_name: ui.name,
    unit: ui.unit || '',
    default_unit_price: Number(ui.price) || 0,
    notes: ui.notes || '',
  };
}

// --- engineer portal (engineer collections — 002/016) ----------------------

// JSON fields can arrive as already-parsed objects (SDK) or strings (raw
// REST / seed round-trips). Normalise both.
function parseJsonField(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function mapEngineerJob(rec) {
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    client_id: rec.client_id,
    engineer_id: rec.engineer_id,
    job_id: rec.job_id || null,
    title: rec.title || '',
    description: rec.description || '',
    address: rec.address || '',
    coordinates: parseJsonField(rec.coordinates),
    status: rec.status || 'assigned',
    priority: rec.priority || 'normal',
    assigned_at: rec.assigned_at || '',
    started_at: rec.started_at || '',
    completed_at: rec.completed_at || '',
    scheduled_at: rec.scheduled_at || '',
    time_on_site_start: rec.time_on_site_start || '',
    time_on_site_end: rec.time_on_site_end || '',
    estimated_duration_minutes: rec.estimated_duration_minutes || null,
    actual_duration_minutes: rec.actual_duration_minutes || null,
    mapping_links: parseJsonField(rec.mapping_links) || {},
    notes: rec.notes || '',
    client: rec.expand?.client_id?.name || '',
    created: rec.created,
  };
}

export function mapPart(rec) {
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    part_number: rec.part_number || '',
    name: rec.name || '',
    description: rec.description || '',
    category: rec.category || '',
    manufacturer: rec.manufacturer || '',
    supplier: rec.supplier || '',
    cost_price: Number(rec.cost_price) || 0,
    sell_price: Number(rec.sell_price) || 0,
    stock_quantity: Number(rec.stock_quantity) || 0,
    reorder_level: Number(rec.reorder_level) || 0,
    unit: rec.unit || 'each',
    location: rec.location || '',
    specifications: parseJsonField(rec.specifications) || {},
    image: Array.isArray(rec.image) ? rec.image[0] : rec.image || '',
    datasheet: Array.isArray(rec.datasheet) ? rec.datasheet[0] : rec.datasheet || '',
    status: rec.status || 'active',
  };
}

export function mapFault(rec) {
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    fault_code: rec.fault_code || '',
    name: rec.name || '',
    description: rec.description || '',
    category: rec.category || '',
    equipment_type: rec.equipment_type || '',
    symptoms: parseJsonField(rec.symptoms) || [],
    troubleshooting_steps: rec.troubleshooting_steps || '',
    common_causes: rec.common_causes || '',
    required_parts: parseJsonField(rec.required_parts) || [],
    estimated_time_minutes: rec.estimated_time_minutes || null,
    difficulty_level: rec.difficulty_level || null,
    priority: rec.priority || 'medium',
    safety_hazard: Boolean(rec.safety_hazard),
    safety_notes: rec.safety_notes || '',
  };
}

export function mapEngineerDoc(rec) {
  const file = Array.isArray(rec.file) ? rec.file[0] : rec.file || '';
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    title: rec.title || '',
    description: rec.description || '',
    category: rec.category || '',
    document_type: rec.document_type || 'other',
    file: file,
    version: rec.version || '',
    effective_date: rec.effective_date || '',
    expiry_date: rec.expiry_date || '',
    tags: parseJsonField(rec.tags) || [],
    require_acknowledgement: Boolean(rec.require_acknowledgement),
    status: rec.status || 'active',
    uploaded_by: rec.uploaded_by || '',
    created: rec.created,
  };
}

export function mapExpense(rec) {
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    engineer_id: rec.engineer_id,
    job_id: rec.job_id || null,
    type: rec.type || 'mileage',
    amount: Number(rec.amount) || 0,
    date: rec.date || '',
    notes: rec.notes || '',
    created: rec.created,
  };
}

export function mapEngineerJobSheet(rec) {
  const formData = parseJsonField(rec.form_data) || {};
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    engineer_job_id: rec.engineer_job_id,
    client_id: rec.client_id,
    form_data: formData,
    work_summary: rec.work_summary || '',
    materials_used: rec.materials_used || '',
    // Photos live inside form_data.photos (the engineer UI saves the whole
    // form incl. photos there); the top-level json field is legacy/unused.
    photos: parseJsonField(rec.photos) || (Array.isArray(formData.photos) ? formData.photos : []),
    status: rec.status || 'draft',
    started_at: rec.started_at || '',
    completed_at: rec.completed_at || '',
    submitted_at: rec.submitted_at || '',
    engineer_notes: rec.engineer_notes || '',
    admin_notes: rec.admin_notes || '',
    created: rec.created,
    updated: rec.updated,
    client: rec.expand?.client_id?.name || '',
    engineer_job: rec.expand?.engineer_job_id || null,
  };
}

// --- engineer collections → schema (admin CRUD, update_tasks.md §7) ------

export function partToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    part_number: ui.part_number,
    name: ui.name,
    description: ui.description || '',
    category: ui.category || '',
    manufacturer: ui.manufacturer || '',
    supplier: ui.supplier || '',
    cost_price: Number(ui.cost_price) || 0,
    sell_price: Number(ui.sell_price) || 0,
    stock_quantity: Number(ui.stock_quantity) || 0,
    reorder_level: Number(ui.reorder_level) || 0,
    unit: ui.unit || 'each',
    location: ui.location || '',
    specifications: ui.specifications && Object.keys(ui.specifications).length ? JSON.stringify(ui.specifications) : '{}',
    status: ui.status || 'active',
  };
}

export function faultToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    fault_code: ui.fault_code,
    name: ui.name,
    description: ui.description || '',
    category: ui.category || '',
    equipment_type: ui.equipment_type || '',
    symptoms: JSON.stringify(ui.symptoms || []),
    troubleshooting_steps: ui.troubleshooting_steps || '',
    common_causes: ui.common_causes || '',
    required_parts: JSON.stringify(ui.required_parts || []),
    estimated_time_minutes: ui.estimated_time_minutes == null ? null : Number(ui.estimated_time_minutes),
    difficulty_level: ui.difficulty_level == null ? null : Number(ui.difficulty_level),
    priority: ui.priority || 'medium',
    safety_hazard: Boolean(ui.safety_hazard),
    safety_notes: ui.safety_notes || '',
  };
}

export function engineerDocToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    title: ui.title,
    description: ui.description || '',
    category: ui.category || '',
    document_type: ui.document_type || 'other',
    version: ui.version || '',
    effective_date: ui.effective_date || null,
    expiry_date: ui.expiry_date || null,
    tags: JSON.stringify(ui.tags || []),
    require_acknowledgement: Boolean(ui.require_acknowledgement),
    status: ui.status || 'active',
  };
}

export function expenseToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    engineer_id: ui.engineer_id,
    job_id: ui.job_id || null,
    type: ui.type,
    amount: Number(ui.amount) || 0,
    date: ui.date,
    notes: ui.notes || '',
  };
}

export function mapEtaUpdate(rec) {
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    client_id: rec.client_id,
    engineer_id: rec.engineer_id,
    engineer_job_id: rec.engineer_job_id,
    status: rec.status || 'on_way',
    eta_minutes: rec.eta_minutes ?? null,
    message: rec.message || '',
    current_location: parseJsonField(rec.current_location) || null,
    notification_sent: Boolean(rec.notification_sent),
    notified_at: rec.notified_at || '',
    created_at: rec.created_at || rec.created || '',
  };
}

export function mapEngineerLocation(rec) {
  return {
    id: rec.id,
    tenant_id: rec.tenant_id,
    engineer_id: rec.engineer_id,
    engineer_job_id: rec.engineer_job_id || null,
    coordinates: parseJsonField(rec.coordinates) || {},
    address: rec.address || '',
    accuracy: rec.accuracy || '',
    timestamp: rec.timestamp || rec.created || '',
    notes: rec.notes || '',
  };
}

// --- helpers ------------------------------------------------------------

function initials(name = '') {
  return name.split(' ').map((word) => word[0]).join('').slice(0, 2).toUpperCase() || '??';
}

// --- line item templates (task 1.5) ---------------------------------------
// One-click starter line-item sets for common jobs (boiler service,
// landlord gas cert, call-out + parts). Stored per-tenant.

export function mapLineItemTemplate(rec) {
  // Items can arrive as JSON string or already-parsed array — normalize.
  let items = [];
  try {
    items = typeof rec.items === 'string' ? JSON.parse(rec.items) : (rec.items || []);
  } catch {
    items = [];
  }
  // Ensure items is an array of objects with expected keys.
  if (!Array.isArray(items)) items = [];
  items = items.map((item, index) => ({
    description: item?.description || '',
    qty: Number(item?.qty) || 1,
    unit_price: Number(item?.unit_price) || 0,
    total: (Number(item?.qty) || 1) * (Number(item?.unit_price) || 0),
    id: `${rec.id}_tpl_${index}`,
  }));

  return {
    id: rec.id,
    tenant_id: rec.tenant_id || '',
    name: rec.name || '',
    items,
    sort: rec.sort ?? 0,
    created: rec.created || '',
    updated: rec.updated || '',
  };
}

export function lineItemTemplateToSchema(ui, tenantId) {
  return {
    tenant_id: tenantId,
    name: ui.name,
    // Stringify items for PocketBase JSON field.
    items: JSON.stringify((ui.items || []).map((item) => ({
      description: item.description || '',
      qty: Number(item.qty) || 1,
      unit_price: Number(item.unit_price) || 0,
    }))),
    sort: ui.sort ?? 0,
  };
}
